import { useState, useMemo } from "react";
import { Checkbox as AntCheckbox, DatePicker, Select as AntSelect, Spin } from "antd";
import {
  FiCalendar,
  FiDownloadCloud,
  FiFilter,
  FiPlusCircle,
  FiTrash2,
} from "react-icons/fi";
import MainLayout from "../../layout/MainLayout";
import { ToggleButton } from "../index";
import billToIcon from "../../assets/images/bill -to.png";
import billFromIcon from "../../assets/images/from.png";
import { useInvoices, useTransactions } from "../../data/live";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";

const fmtAmount = (n, ccy) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${ccy || ""}`;

const shortText = (value, max = 15) => {
  const text = String(value || "-");
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const initials = (value) =>
  String(value || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-AU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const Statement = () => {
  const { items: transactions, loading: txLoading } = useTransactions();
  const { items: invoices, loading: invLoading } = useInvoices();
  const [tab, setTab] = useState("My Invoice");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fileFormat, setFileFormat] = useState("all");
  const [sortBy, setSortBy] = useState("date-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [deletedKeys, setDeletedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);


  const invoiceRows = useMemo(
    () =>
      invoices.map((inv, index) => ({
        key: `invoice-${inv.id || inv.invoiceNumber || index}`,
        billFrom: { icon: billFromIcon, name: "ARM Financial" },
        billTo: { icon: billToIcon, name: inv.clientName || "-" },
        invoiceNo: inv.invoiceNumber || inv.id?.slice(0, 8) || "INV-0000",
        dateTime: formatDate(inv.createdAt),
        rawDate: inv.createdAt,
        amount: fmtAmount(inv.totalAmount, inv.currency),
        amountValue: Number(inv.totalAmount || 0),
        category: inv.status || "DRAFT",
        tags: ["Invoice"],
      })),
    [invoices]
  );
  const txRows = useMemo(
    () =>
      transactions.map((t, index) => ({
        key: `report-${t.id || t.reference || index}`,
        billFrom: {
          icon: billFromIcon,
          name: t.direction === "in" ? t.counterparty || "-" : "ARM Wallet",
        },
        billTo: {
          icon: billToIcon,
          name: t.direction === "out" ? t.counterparty || "-" : "ARM Wallet",
        },
        invoiceNo: t.reference || "-",
        dateTime: formatDate(t.date),
        rawDate: t.date,
        amount: fmtAmount(t.amount, t.currency),
        amountValue: Number(t.amount || 0),
        category: t.category || t.type || "-",
        tags: [t.status || "-"],
      })),
    [transactions]
  );

  const customerRows = useMemo(() => {
    const groups = {};
    transactions.forEach((t) => {
      const key = t.counterparty || "Unknown";
      if (!groups[key]) {
        groups[key] = {
          count: 0,
          total: 0,
          currency: t.currency,
          lastDate: t.date,
        };
      }
      groups[key].count += 1;
      groups[key].total += t.amount || 0;
      if (new Date(t.date) > new Date(groups[key].lastDate)) {
        groups[key].lastDate = t.date;
      }
    });
    return Object.entries(groups).map(([name, g]) => ({
      key: `customer-${name}`,
      billFrom: { icon: billFromIcon, name: "ARM Financial" },
      billTo: { icon: billToIcon, name },
      invoiceNo: `${g.count} txns`,
      dateTime: formatDate(g.lastDate),
      rawDate: g.lastDate,
      amount: fmtAmount(g.total, g.currency),
      amountValue: Number(g.total || 0),
      category: "Customer",
      tags: ["Aggregated"],
    }));
  }, [transactions]);

  const tableBody = useMemo(() => {
    const rows =
      tab === "My Invoice" ? invoiceRows : tab === "My Customers" ? customerRows : txRows;
    return rows.filter((row) => !deletedKeys.includes(row.key));
  }, [customerRows, deletedKeys, invoiceRows, tab, txRows]);

  const loading = tab === "My Invoice" ? invLoading : txLoading;

  const filteredRows = useMemo(() => {
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;

    return [...tableBody]
      .filter((row) => {
        if (fileFormat !== "all" && row.category !== fileFormat) return false;
        if (!fromTime && !toTime) return true;
        const rowTime = new Date(row.rawDate).getTime();
        if (Number.isNaN(rowTime)) return true;
        if (fromTime && rowTime < fromTime) return false;
        if (toTime && rowTime > toTime) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === "amount-asc") return a.amountValue - b.amountValue;
        if (sortBy === "amount-desc") return b.amountValue - a.amountValue;
        if (sortBy === "invoice-asc") return a.invoiceNo.localeCompare(b.invoiceNo);
        if (sortBy === "invoice-desc") return b.invoiceNo.localeCompare(a.invoiceNo);
        const aTime = new Date(a.rawDate).getTime() || 0;
        const bTime = new Date(b.rawDate).getTime() || 0;
        return sortBy === "date-asc" ? aTime - bTime : bTime - aTime;
      });
  }, [dateFrom, dateTo, fileFormat, sortBy, tableBody]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = filteredRows.length ? (currentPage - 1) * pageSize : 0;
  const pagedRows = filteredRows.slice(startIndex, startIndex + pageSize);
  const pageKeys = pagedRows.map((row) => row.key);
  const selectedPageKeys = pageKeys.filter((key) => selectedKeys.includes(key));
  const allPageSelected = pageKeys.length > 0 && selectedPageKeys.length === pageKeys.length;
  const somePageSelected = selectedPageKeys.length > 0 && !allPageSelected;
  const selectedCount = selectedKeys.length;

  const categoryOptions = useMemo(
    () => Array.from(new Set(tableBody.map((row) => row.category).filter(Boolean))),
    [tableBody]
  );

  const updateTab = (nextTab) => {
    setTab(nextTab);
    setPage(1);
    setSelectedKeys([]);
  };

  const toggleRow = (key) => {
    setSelectedKeys((current) =>
      current.includes(key)
        ? current.filter((selectedKey) => selectedKey !== key)
        : [...current, key]
    );
  };

  const togglePage = (checked) => {
    setSelectedKeys((current) => {
      if (!checked) return current.filter((key) => !pageKeys.includes(key));
      return Array.from(new Set([...current, ...pageKeys]));
    });
  };

  const deleteSelectedRows = () => {
    setDeletedKeys((current) => Array.from(new Set([...current, ...selectedKeys])));
    setSelectedKeys([]);
    setPage(1);
  };

  return (
    <MainLayout
      NavName="Statement"
      expand={false}
      pageClassName="dashboard-page report-theme-page statement-page"
      contentClassName="dashboard-content"
    >
      <div className="statement-shell">
        <div className="statement-heading-row">
          <h2>Statements Details</h2>
          <QuickActionSelect />
        </div>

        <div className="statement-card">
          <div className="statement-card-toolbar">
            <ToggleButton
              options={["My Invoice", "My Customers", "My Report"]}
              defaultValue={tab}
              onChange={updateTab}
            />

            <div className="statement-filter-row">
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="statement-delete-btn"
                  onClick={deleteSelectedRows}
                >
                  <FiTrash2 />
                  <span>Delete ({selectedCount})</span>
                </button>
              )}

              <DatePicker
                className="statement-ant-control statement-ant-datepicker"
                suffixIcon={<FiCalendar />}
                placeholder="Date From"
                format="YYYY-MM-DD"
                onChange={(date) => {
                  setDateFrom(date ? date.format("YYYY-MM-DD") : "");
                  setPage(1);
                }}
              />
              <DatePicker
                className="statement-ant-control statement-ant-datepicker"
                suffixIcon={<FiCalendar />}
                placeholder="Date To"
                format="YYYY-MM-DD"
                onChange={(date) => {
                  setDateTo(date ? date.format("YYYY-MM-DD") : "");
                  setPage(1);
                }}
              />
              <AntSelect
                className="statement-ant-select"
                value={fileFormat}
                suffixIcon={<FiCalendar />}
                onChange={(value) => {
                  setFileFormat(value);
                  setPage(1);
                }}
                options={[
                  { label: "File Format", value: "all" },
                  ...categoryOptions.map((category) => ({
                    label: category,
                    value: category,
                  })),
                ]}
              />
              <AntSelect
                className="statement-ant-select statement-sort-select"
                value={sortBy}
                suffixIcon={<FiFilter />}
                onChange={(value) => {
                  setSortBy(value);
                  setPage(1);
                }}
                options={[
                  { label: "Sort: Newest", value: "date-desc" },
                  { label: "Sort: Oldest", value: "date-asc" },
                  { label: "Amount: High", value: "amount-desc" },
                  { label: "Amount: Low", value: "amount-asc" },
                  { label: "Invoice: A-Z", value: "invoice-asc" },
                  { label: "Invoice: Z-A", value: "invoice-desc" },
                ]}
              />
            </div>
          </div>

          <div className="statement-card-body">
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <Spin size="large" />
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="statement-empty-state">No {tab.toLowerCase()} yet.</div>
            ) : (
              <>
                <div className="statement-table-wrap">
                  <table className="statement-data-table">
                    <thead>
                      <tr>
                        <th>
                          <AntCheckbox
                            checked={allPageSelected}
                            indeterminate={somePageSelected}
                            onChange={(e) => togglePage(e.target.checked)}
                          />
                        </th>
                        <th>From</th>
                        <th>Billed To</th>
                        <th>Invoice Number</th>
                        <th>Date and time</th>
                        <th>Amount</th>
                        <th>Category</th>
                        <th>Tags</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRows.map((row) => (
                        <tr
                          key={row.key}
                          className={selectedKeys.includes(row.key) ? "is-selected" : ""}
                        >
                          <td>
                            <AntCheckbox
                              checked={selectedKeys.includes(row.key)}
                              onChange={() => toggleRow(row.key)}
                            />
                          </td>
                          <td>
                            <div className="statement-person-cell">
                              <span className="statement-avatar statement-avatar-green">
                                {initials(row.billFrom.name)}
                              </span>
                              <strong title={row.billFrom.name}>{shortText(row.billFrom.name)}</strong>
                            </div>
                          </td>
                          <td>
                            <div className="statement-person-cell">
                              <span className="statement-avatar statement-avatar-dark">
                                {initials(row.billTo.name)}
                              </span>
                              <strong title={row.billTo.name}>{shortText(row.billTo.name)}</strong>
                            </div>
                          </td>
                          <td>{row.invoiceNo}</td>
                          <td>{shortText(row.dateTime, 13)}</td>
                          <td className="statement-amount-cell">{row.amount}</td>
                          <td>{row.category}</td>
                          <td>
                            <div className="statement-tags-cell">
                              {row.tags.map((tag) => (
                                <span key={tag}>{tag}</span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <div className="statement-action-cell">
                              <button type="button" aria-label="Add tag">
                                <FiPlusCircle />
                              </button>
                              <button type="button" aria-label="Download invoice">
                                <FiDownloadCloud />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="statement-pagination-row">
                  <span>
                    Showing {startIndex + 1}-
                    {Math.min(startIndex + pageSize, filteredRows.length)} of {filteredRows.length}
                  </span>
                  <div className="statement-pagination-controls">
                    <AntSelect
                      className="statement-page-size-select"
                      value={pageSize}
                      onChange={(value) => {
                        setPageSize(value);
                        setPage(1);
                      }}
                      options={[5, 8, 10].map((size) => ({
                        label: `${size} / page`,
                        value: size,
                      }))}
                    />
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setPage((value) => Math.max(1, value - 1))}
                    >
                      Previous
                    </button>
                    <strong>
                      {currentPage} / {totalPages}
                    </strong>
                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Statement;
